import { useState } from "react";
import axios from "axios";
import { Type, Loader2, Sparkles, AlertCircle, Copy, Check, Trophy, Image, Tag, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreBadge = (s) => (s >= 80 ? "bg-green-100 text-green-700" : s >= 60 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700");

const CopyButton = ({ text, id, copied, onCopy }) => (
  <button
    data-testid={`copy-${id}`}
    onClick={() => onCopy(id, text)}
    title="Copiar"
    className="p-1.5 rounded-lg hover:bg-purple-100 text-gray-400 hover:text-purple-600 transition-all flex-shrink-0"
  >
    {copied === id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
  </button>
);

export default function TitleGenerator({ analysis, canGenerate, onLogin }) {
  const [data, setData] = useState(analysis.titles_pack || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  const handleCopy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      window.prompt("Copia el texto:", text);
    }
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/analysis/${analysis.id}/generate-titles`);
      setData(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No se pudieron generar los títulos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="title-generator-section" className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Type className="w-5 h-5 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-800">Generador de Títulos y SEO</h3>
        </div>
        {!data && !loading && canGenerate && (
          <button
            data-testid="generate-titles-button"
            onClick={generate}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Generar Títulos y SEO con IA
          </button>
        )}
        {!data && !loading && !canGenerate && (
          <button data-testid="titles-login-button" onClick={onLogin} className="px-5 py-2.5 rounded-xl font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-all">
            Inicia sesión para generar títulos
          </button>
        )}
      </div>

      {!data && !loading && !error && (
        <p className="text-sm text-gray-500">10 títulos virales con score, 5 descripciones SEO, 25 etiquetas y 5 ideas de miniatura — listos para copiar en YouTube.</p>
      )}

      {loading && (
        <div data-testid="titles-loading" className="text-center py-10">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Generando títulos, descripciones y etiquetas... (~20-40 seg)</p>
        </div>
      )}

      {error && (
        <div data-testid="titles-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div data-testid="titles-results" className="space-y-6 animate-fadeIn">
          {/* Mejor título */}
          {data.best_title && (
            <div data-testid="best-title" className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-yellow-600" />
                <span className="text-xs font-bold text-yellow-700 uppercase">Recomendado para superar el 10% de CTR</span>
                {data.best_title.estimated_ctr && (
                  <span className="text-xs font-bold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full">CTR est. {data.best_title.estimated_ctr}</span>
                )}
              </div>
              <div className="flex items-start justify-between gap-2">
                <p className="font-bold text-gray-900 text-lg">{data.best_title.title}</p>
                <CopyButton text={data.best_title.title} id="best" copied={copied} onCopy={handleCopy} />
              </div>
              <p className="text-sm text-gray-600 mt-2"><b>¿Por qué?</b> {data.best_title.reason}</p>
            </div>
          )}

          {/* 10 títulos */}
          {data.titles?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">10 títulos optimizados para CTR</h4>
              <div className="space-y-2">
                {data.titles.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${scoreBadge(t.viral_score)}`}>{t.viral_score}</span>
                    <p className="flex-1 text-sm font-medium text-gray-800">{t.title}</p>
                    {t.style && <span className="hidden sm:block text-[10px] uppercase text-purple-500 font-bold flex-shrink-0">{t.style}</span>}
                    <CopyButton text={t.title} id={`title-${i}`} copied={copied} onCopy={handleCopy} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Descripciones */}
            {data.descriptions?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-1"><FileText className="w-4 h-4 text-purple-600" /> 5 descripciones SEO</h4>
                <div data-testid="descriptions-list" className="space-y-2">
                  {data.descriptions.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <p className="flex-1 text-sm text-gray-700">{d}</p>
                      <CopyButton text={d} id={`desc-${i}`} copied={copied} onCopy={handleCopy} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Miniaturas */}
            {data.thumbnail_ideas?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-1"><Image className="w-4 h-4 text-purple-600" /> 5 ideas de miniatura</h4>
                <div data-testid="thumbnail-ideas-list" className="space-y-2">
                  {data.thumbnail_ideas.map((th, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <p className="text-sm font-semibold text-gray-800">{th.concept}</p>
                      <p className="text-xs text-gray-500 mt-1">{th.elements}</p>
                      {th.text_overlay && <p className="text-xs mt-1"><span className="font-bold text-purple-600">Texto:</span> <span className="text-gray-700">&ldquo;{th.text_overlay}&rdquo;</span></p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          {data.tags?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-700 uppercase flex items-center gap-1"><Tag className="w-4 h-4 text-purple-600" /> 25 etiquetas SEO</h4>
                <button
                  data-testid="copy-all-tags"
                  onClick={() => handleCopy("all-tags", data.tags.join(", "))}
                  className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg border border-purple-200 transition-all"
                >
                  {copied === "all-tags" ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copiar todas
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full border border-purple-100 font-medium">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
